import React, { useState, useEffect } from 'react';
import {
  Drawer,
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getAgentPrompt, saveAgentPrompt } from '../../services/agentApi';

const DRAWER_WIDTH = 700;

const PROMPT_TYPES = {
  AGENT_SYSTEM: 'agent_system_prompt',
  TOOL_KB_LOOKUP: 'tool_knowledge_base_lookup',
};

const AgentPromptsDrawer = ({ open, onClose, tenantId, agent }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [agentSystemPrompt, setAgentSystemPrompt] = useState('');
  const [toolKbDescription, setToolKbDescription] = useState('');
  const [error, setError] = useState(null);

  // Use tenant_agent_instance_id so each tenant agent instance has its own prompts; fallback for legacy
  const agentKey = agent?.tenantAgentInstanceId ?? agent?.agentUniqueId ?? agent?.agentName ?? null;

  useEffect(() => {
    if (open && tenantId) {
      loadPrompts();
    } else if (!open) {
      setAgentSystemPrompt('');
      setToolKbDescription('');
      setError(null);
    }
  }, [open, tenantId, agentKey]);

  const loadPrompts = async () => {
    if (!agentKey) return;
    setLoading(true);
    setError(null);
    try {
      const [systemRes, toolRes] = await Promise.all([
        getAgentPrompt(PROMPT_TYPES.AGENT_SYSTEM, tenantId, agentKey),
        getAgentPrompt(PROMPT_TYPES.TOOL_KB_LOOKUP, tenantId, agentKey),
      ]);
      setAgentSystemPrompt(systemRes?.found && systemRes.prompt_text ? systemRes.prompt_text : '');
      setToolKbDescription(toolRes?.found && toolRes.prompt_text ? toolRes.prompt_text : '');
    } catch (err) {
      console.error('Load prompts error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load prompts');
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSystemPrompt = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveAgentPrompt(PROMPT_TYPES.AGENT_SYSTEM, {
        prompt_text: agentSystemPrompt,
        tenantId: tenantId || undefined,
        agentName: agentKey || undefined,
      });
      toast.success('Agent system prompt saved');
    } catch (err) {
      console.error('Save system prompt error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save');
      toast.error('Failed to save agent system prompt');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveToolDescription = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveAgentPrompt(PROMPT_TYPES.TOOL_KB_LOOKUP, {
        prompt_text: toolKbDescription,
        tenantId: tenantId || undefined,
        agentName: agentKey || undefined,
      });
      toast.success('Knowledge base lookup description saved');
    } catch (err) {
      console.error('Save tool description error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save');
      toast.error('Failed to save tool description');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6">
            Agent Prompts{agent?.agentName ? ` — ${agent.agentName}` : ''}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Agent system prompt */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Agent system prompt
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Use placeholders: {'{{current_date_str}}'}, {'{{current_datetime_iso}}'}, {'{{schema_info}}'}
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={12}
                maxRows={24}
                value={agentSystemPrompt}
                onChange={(e) => setAgentSystemPrompt(e.target.value)}
                placeholder="Enter the main agent system prompt (with placeholders above)..."
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveSystemPrompt}
                disabled={saving}
                sx={{ mb: 3 }}
              >
                Save system prompt
              </Button>

              <Divider sx={{ my: 3 }} />

              {/* Tool: knowledge_base_lookup description */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Knowledge base lookup (tool description)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Short description telling the agent when to use the knowledge_base_lookup tool.
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={12}
                value={toolKbDescription}
                onChange={(e) => setToolKbDescription(e.target.value)}
                placeholder="e.g. Use this tool for specifications, knowledge base, documentation..."
                variant="outlined"
                size="small"
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveToolDescription}
                disabled={saving}
              >
                Save tool description
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AgentPromptsDrawer;
